import { forwardRef } from 'react';
import MuiTypography, { type TypographyProps } from '@mui/material/Typography';
import * as stylex from '@stylexjs/stylex';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

export type { TypographyProps };

// MUI's Typography is polymorphic via `component`, but wrapping it in a
// forwardRef with a fixed props type loses that inference. Allow the HTML
// attributes we rely on when rendering as `label`/`button` to pass through.
type PolymorphicHtmlProps = Pick<React.LabelHTMLAttributes<HTMLElement>, 'htmlFor'> &
  Pick<React.ButtonHTMLAttributes<HTMLElement>, 'type' | 'disabled'>;

type AppTypographyRootProps = TypographyProps & Partial<PolymorphicHtmlProps>;

type AppTypographyProps = AppTypographyRootProps & {
  text?: string;
};

function omitFplTypographySxProps(sx: TypographyProps['sx']): TypographyProps['sx'] {
  if (sx == null || typeof sx !== 'object' || Array.isArray(sx)) {
    return sx;
  }

  const {
    fontFamily: _fontFamily,
    fontSize: _fontSize,
    fontWeight: _fontWeight,
    lineHeight: _lineHeight,
    letterSpacing: _letterSpacing,
    ...rest
  } = sx as Record<string, unknown>;
  return rest;
}

export const typographyStyles = stylex.create({
  textHeadingLarge: stylex.env.mixin.fpl.typography.textHeadingLarge,
  textHeadingMedium: stylex.env.mixin.fpl.typography.textHeadingMedium,
  textHeadingSmall: stylex.env.mixin.fpl.typography.textHeadingSmall,
  textBodyLargeStrong: stylex.env.mixin.fpl.typography.textBodyLargeStrong,
  textBodyLarge: stylex.env.mixin.fpl.typography.textBodyLarge,
  textBodyMediumStrong: stylex.env.mixin.fpl.typography.textBodyMediumStrong,
  textBodyMedium: stylex.env.mixin.fpl.typography.textBodyMedium,
  textBodySmall: stylex.env.mixin.fpl.typography.textBodySmall,
  textBodySmallStrong: stylex.env.mixin.fpl.typography.textBodySmallStrong,
});

const typographyVariantStyles = {
  h1: typographyStyles.textHeadingLarge,
  h2: typographyStyles.textHeadingMedium,
  h3: typographyStyles.textHeadingSmall,
  'body-xxl-rg': typographyStyles.textHeadingLarge,
  'body-xl-rg': typographyStyles.textHeadingMedium,
  'body-lg-sb': typographyStyles.textBodyLargeStrong,
  'body-lg-md': typographyStyles.textBodyLargeStrong,
  'body-lg-rg': typographyStyles.textBodyLarge,
  'body-std-md': typographyStyles.textBodyMediumStrong,
  'body-std-rg': typographyStyles.textBodyMedium,
  'body-sm-md': typographyStyles.textBodyMedium,
  'body-sm-rg': typographyStyles.textBodyMedium,
  'body-xs-rg': typographyStyles.textBodySmall,
  'label-sm-rg': typographyStyles.textBodySmallStrong,
  'label-xs-rg': typographyStyles.textBodySmallStrong,
};

type AppTypographyVariant = keyof typeof typographyVariantStyles;

function isAppTypographyVariant(variant: TypographyProps['variant']): variant is AppTypographyVariant {
  return typeof variant === 'string' && variant in typographyVariantStyles;
}

const AppTypography = forwardRef<HTMLElement, AppTypographyRootProps>(
  ({ className, style, sx, variant = 'body-std-rg', color: colorProp, ...props }, ref) => {
    const isFplMigrationEnabled = useFplMigrationFlag();

    if (!isFplMigrationEnabled) {
      return (
        <MuiTypography
          {...props}
          ref={ref}
          className={className}
          style={style}
          sx={sx}
          variant={variant}
          color={colorProp}
        />
      );
    }

    const mappedColor = colorProp === token.textDanger ? cssVars.color.textDanger : colorProp;
    const variantStyle = isAppTypographyVariant(variant) ? typographyVariantStyles[variant] : undefined;
    const stylexProps = variantStyle ? stylex.props(variantStyle) : {};
    const mergedClassName = [stylexProps.className, className].filter(Boolean).join(' ');
    const mergedStyle = stylexProps.style ? { ...stylexProps.style, ...style } : style;
    const sxWithoutTypographyOverrides = omitFplTypographySxProps(sx);

    return (
      <MuiTypography
        {...props}
        ref={ref}
        className={mergedClassName || undefined}
        component={props.component ?? 'span'}
        style={mergedStyle}
        sx={sxWithoutTypographyOverrides}
        variant="unstyled"
        color={mappedColor}
      />
    );
  },
);

AppTypography.displayName = 'AppTypography';

export const Typography = AppTypography;

export const TextStdRg = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="body-std-rg" color={token.textSecondary} {...props}>
    {text || children}
  </Typography>
);

export const TextStdMd = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="body-std-md" {...props}>
    {text || children}
  </Typography>
);

export const TextLgRg = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="body-lg-rg" {...props}>
    {text || children}
  </Typography>
);

export const TextLgMd = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="body-lg-md" {...props}>
    {text || children}
  </Typography>
);

export const TextLgSb = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="body-lg-sb" {...props}>
    {text || children}
  </Typography>
);

export const TextSmRg = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="body-sm-rg" {...props}>
    {text || children}
  </Typography>
);

export const TextSmMd = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="body-sm-md" {...props}>
    {text || children}
  </Typography>
);

export const LabelSmRg = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="label-sm-rg" {...props}>
    {text || children}
  </Typography>
);

export const H1 = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="h1" {...props}>
    {text || children}
  </Typography>
);

export const H2 = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="h2" {...props}>
    {text || children}
  </Typography>
);

export const H3 = ({ text, children, ...props }: AppTypographyProps) => (
  <Typography variant="h3" {...props}>
    {text || children}
  </Typography>
);
